import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDebounce } from '@react-hook/debounce';
import { Input } from 'components/ui/input';
import { Icon } from 'components/ui/icon';
import { cn } from 'lib/utils';
import useQuery from 'utils/hooks/useQuery';
import QUERY from 'utils/query';

interface SearchProps {
  queryParams?: string;
  placeHolders?: string;
  className?: string;
}

const Search = ({
  queryParams = '',
  placeHolders,
  className,
}: SearchProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useQuery(queryParams);

  const [query, setQuery] = useDebounce<string | null>(null, 600);
  const [searchString, setSearchString] = useState<string>('');

  const reset = () => {
    setQuery(null);
    setSearchString('');
  };

  const clearAll = () => {
    reset();
    navigate({
      search: QUERY.update(location.search, { search: null }),
    });
  };

  // Keep the latest location.search readable inside the effect below without
  // listing it as a dependency — otherwise the sync effect would re-run (and
  // re-navigate) on every URL change instead of only when `query` changes.
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;

  useEffect(() => {
    const value = urlParams.get('search');
    setSearchString(value ?? '');
  }, [queryParams]);

  // Push the debounced query into the URL whenever it changes.
  useEffect(() => {
    if (query) {
      navigate({
        search: QUERY.update(locationSearchRef.current, { search: query }),
      });
    }
  }, [query, navigate]);

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={cn('relative w-72', className)}
    >
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-blue-50 pointer-events-none">
        <Icon name="search" size={16} aria-hidden="true" />
      </span>
      <Input
        aria-label="Search"
        type="search"
        placeholder={placeHolders ?? 'Search...'}
        value={searchString}
        onChange={(e) => {
          if (e.target.value.length === 0) {
            clearAll();
          }
          setSearchString(e.target.value);
          setQuery(e.target.value);
        }}
        className="h-9 pl-9 pr-9 text-sm bg-white border-gray-blue-10 placeholder:text-gray-blue-50 focus-visible:border-green-400 focus-visible:ring-green-400"
      />
      {searchString.length > 0 && (
        <button
          type="button"
          data-testid="Clear"
          onClick={clearAll}
          aria-label="Clear search"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-blue-50 hover:text-gray-blue-80 bg-transparent border-0 p-0 cursor-pointer"
        >
          <Icon name="x" size={16} aria-hidden="true" />
        </button>
      )}
    </form>
  );
};

export default Search;
